import type * as React from 'react'
import { useState } from 'react'
import type { JSX } from 'react'
import { clsx } from 'clsx'

import {
  Button,
  ChannelAvatar,
  CheckIcon,
  CounterInput,
  Dialog,
  Flex,
  Label,
  Notice,
  PencilIcon,
  RadioCard,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'
import type { ChannelType } from '@buffer-mono/popcorn'

import { useUser } from '../../../../common/context/User'
import { DEFAULT_POSTING_GOAL, POSTING_GOAL_OPTIONS } from './constants'
import { Confetti } from './Confetti'
import {
  useChannelConnectionTracking,
  trackPostingGoalSkipped,
} from './hooks/useChannelConnectionTracking'
import { usePostingGoalMutation } from './hooks/usePostingGoalMutation'

import styles from './PostingGoalConfiguration.module.css'

function PostingGoalConfiguration({
  channelIds: passedChannelIds,
  dismissModal,
  remindLater = false,
  isSuccessConnection = false,
  onPostingGoalUpdated,
  returnFocusToId,
}: {
  channelIds?: string[]
  dismissModal: () => void
  remindLater?: boolean
  isSuccessConnection?: boolean
  onPostingGoalUpdated?: () => void
  returnFocusToId?: string
}): JSX.Element {
  const account = useUser()
  const organization = account?.currentOrganization

  const [selectedOption, setSelectedOption] = useState<string>(
    DEFAULT_POSTING_GOAL.goal.toString(),
  )
  const [selectedGoal, setSelectedGoal] = useState<string>(
    DEFAULT_POSTING_GOAL.goal.toString(),
  )

  const { channels, channel, loading, error, createPostingGoal } =
    usePostingGoalMutation({
      channelIds: passedChannelIds,
      overridePostingSchedules: isSuccessConnection,
    })

  useChannelConnectionTracking({
    organizationId: isSuccessConnection ? organization?.id : undefined,
    channel: isSuccessConnection ? channel : undefined,
    commonTrackingProperties: organization?.commonTrackingProperties,
  })

  const isMobile = useIsMobileScreenSize()

  if (!channel) {
    return <></>
  }

  const channelIds = channels.map((c) => c.id)

  const handleCreatePostingPlan = async (): Promise<void> => {
    const wasCreated = await createPostingGoal(Number(selectedGoal))
    if (!wasCreated) {
      return
    }
    onPostingGoalUpdated?.()
    dismissModal()
  }

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      handleRemindLater()
    }
  }

  const handleRemindLater = (): void => {
    if (isSuccessConnection) {
      trackPostingGoalSkipped({
        organizationId: organization?.id,
        channel,
        commonTrackingProperties: organization?.commonTrackingProperties,
      })
    }
    dismissModal()
  }

  const handleCloseAutoFocus = (event: Event): void => {
    if (!returnFocusToId) {
      return
    }
    event.preventDefault()
    document.getElementById(returnFocusToId)?.focus()
  }

  const dialogDescription =
    channelIds.length > 1
      ? "We'll add recommended posting times to channels without a schedule to help you reach your goal."
      : isSuccessConnection
        ? "We'll add recommended posting times to your queue to help you reach your goal."
        : "We'll add recommended posting times to your queue if you don't have any to help you reach your goal."

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <Dialog.Content
        size="xlarge"
        className={styles.content}
        onCloseAutoFocus={handleCloseAutoFocus}
      >
        <Dialog.CloseButton />
        <Dialog.Header className={styles.header}>
          {isSuccessConnection && (
            <Confetti
              className={styles.confetti}
              duration={5000}
              startDelay={300}
            />
          )}
          <div className={styles.avatarWrapper}>
            {channelIds?.length > 1 ? (
              <ChannelAvatar.Stack
                stacking="firstOnTop"
                max={5}
                style={
                  {
                    '--avatar-stack-margin-left': '-28px',
                  } as React.CSSProperties
                }
              >
                {channels.map((channel) => (
                  <ChannelAvatar
                    key={channel.id}
                    channel={channel.service as ChannelType}
                    src={channel.avatar}
                    alt={channel.name}
                    size="large"
                    className={styles.avatar}
                  />
                ))}
              </ChannelAvatar.Stack>
            ) : (
              <ChannelAvatar
                channel={channel.service as ChannelType}
                src={channel.avatar}
                alt={channel.name}
                size="large"
                className={styles.avatar}
                style={
                  {
                    '--channel-avatar-border-width': '2px',
                  } as React.CSSProperties
                }
              />
            )}
            {isSuccessConnection && (
              <div className={styles.check}>
                <CheckIcon color="inverted" />
              </div>
            )}
          </div>
          <Dialog.Title size={isMobile ? 'md' : 'lg'} className={styles.title}>
            {isSuccessConnection
              ? 'All Done! Get Started With a Posting Goal'
              : 'Stay Consistent With a Posting Goal'}
          </Dialog.Title>
          <Dialog.Description className={styles.description}>
            {dialogDescription}
          </Dialog.Description>
        </Dialog.Header>
        <form
          onSubmit={(e): void => {
            e.preventDefault()
            handleCreatePostingPlan()
          }}
        >
          <Dialog.Body className={styles.body}>
            {!!error && (
              <Notice variant="error" className={styles.error}>
                There was an error creating your posting goal. Please try again.
              </Notice>
            )}
            <RadioCard.Group
              value={selectedOption}
              onValueChange={(value: string): void => {
                value !== 'custom' && setSelectedGoal(value)
                setSelectedOption(value)
              }}
              className={styles.slimRadioCardGroup}
            >
              {POSTING_GOAL_OPTIONS.map((option) => (
                <RadioCard
                  key={option.goal}
                  value={option.goal.toString()}
                  className={styles.slimRadioCard}
                >
                  <Flex align="center" gap="md">
                    <div
                      className={clsx(
                        styles.goalFrequency,
                        styles[option.color as keyof typeof styles],
                      )}
                    >
                      {option.goal}x
                    </div>
                    <div>
                      <Label
                        size={isMobile ? 'small' : 'large'}
                        className={styles.title}
                      >
                        {option.title}
                      </Label>
                    </div>
                  </Flex>
                </RadioCard>
              ))}
              <RadioCard
                key="custom"
                value="custom"
                className={styles.slimRadioCard}
              >
                <Flex align="center" gap="md" className={styles.customGoal}>
                  <div className={clsx(styles.goalFrequency, styles.pink)}>
                    <PencilIcon />
                  </div>
                  <Flex
                    align="center"
                    justify="between"
                    gap="md"
                    className={styles.customGoal}
                  >
                    <Label
                      size={isMobile ? 'small' : 'large'}
                      className={styles.title}
                    >
                      Choose your goal
                    </Label>
                    {selectedOption === 'custom' && (
                      <CounterInput
                        value={Number(selectedGoal)}
                        min={0}
                        max={100}
                        step={1}
                        onChange={(value: number): void =>
                          setSelectedGoal(value.toString())
                        }
                      />
                    )}
                  </Flex>
                </Flex>
              </RadioCard>
            </RadioCard.Group>
          </Dialog.Body>
          <Dialog.Footer showSeparator={false} className={styles.footer}>
            <div className={styles.actionButtons}>
              <Button
                type="submit"
                size="large"
                variant="primary"
                disabled={!selectedGoal || loading}
                loading={loading}
                className={styles.action}
              >
                Commit to Posting Goal
              </Button>
              <Button
                type="button"
                onClick={handleRemindLater}
                size="large"
                variant="tertiary"
                className={styles.action}
              >
                {isSuccessConnection
                  ? 'Skip'
                  : remindLater
                    ? 'Remind Me Later'
                    : 'Cancel'}
              </Button>
            </div>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog>
  )
}

export default PostingGoalConfiguration
